import React, { useEffect, useRef } from "react";
import { colorFor, groupSeries } from "./graphSeries";
import styles from "./SeriesToggles.module.css";

export interface ThresholdLine {
  id: string;
  label: string;
  value: number;
  series?: string;
  note?: string;
}

interface Props {
  names: string[];
  visible: Record<string, boolean>;
  onSeries: (changes: Record<string, boolean>) => void;
  thresholds: ThresholdLine[];
  thresholdOn: Record<string, boolean>;
  onThreshold: (id: string, on: boolean) => void;
}

/** Checkbox that can show a partly-on state. */
function MixedCheckbox({ checked, mixed, onChange, label }: { checked: boolean; mixed: boolean; onChange: (on: boolean) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return <input ref={ref} type="checkbox" checked={checked} aria-label={label} onChange={(e) => onChange(e.target.checked)} />;
}

/** Which graph lines are drawn: one box per column family, expandable to single columns, plus the threshold lines. */
export default function SeriesToggles({ names, visible, onSeries, thresholds, thresholdOn, onThreshold }: Props) {
  const groups = groupSeries(names);
  return (
    <fieldset className={styles.panel} aria-label="Graph lines">
      <legend className={styles.legend}>Graph lines</legend>
      {groups.map(({ family, names: cols }) => {
        const on = cols.filter((n) => visible[n]).length;
        const showThresholds = family.id === "detector" && thresholds.length > 0;
        return (
          <details key={family.id} className={styles.group} open={on > 0 && on < cols.length}>
            <summary className={styles.head}>
              <label className={styles.familyLabel} onClick={(e) => e.stopPropagation()}>
                <MixedCheckbox
                  label={family.title}
                  checked={on === cols.length}
                  mixed={on > 0 && on < cols.length}
                  onChange={(checked) => onSeries(Object.fromEntries(cols.map((n) => [n, checked])))}
                />
                <span className={styles.swatch} style={{ background: family.colors[0] }} aria-hidden="true" />
                {family.title}
              </label>
              <span className={styles.count}>
                {on}/{cols.length}
              </span>
            </summary>
            <p className={styles.desc}>{family.desc}</p>
            <div className={styles.columns}>
              {cols.map((n) => (
                <label key={n} className={styles.column}>
                  <input type="checkbox" checked={!!visible[n]} aria-label={n} onChange={(e) => onSeries({ [n]: e.target.checked })} />
                  <span className={styles.swatch} style={{ background: colorFor(n, names) }} aria-hidden="true" />
                  {n}
                </label>
              ))}
            </div>
            {showThresholds && <ThresholdBoxes thresholds={thresholds} thresholdOn={thresholdOn} onThreshold={onThreshold} />}
          </details>
        );
      })}
      {thresholds.length > 0 && !groups.some((g) => g.family.id === "detector") && (
        <ThresholdBoxes thresholds={thresholds} thresholdOn={thresholdOn} onThreshold={onThreshold} />
      )}
    </fieldset>
  );
}

function ThresholdBoxes({ thresholds, thresholdOn, onThreshold }: Pick<Props, "thresholds" | "thresholdOn" | "onThreshold">) {
  return (
    <div className={styles.thresholds}>
      <p className={styles.desc}>Dashed threshold lines. Compare a Detector line to its threshold to see why a rep did or didn't count.</p>
      {thresholds.map((t) => (
        <label key={t.id} className={styles.column} title={t.note}>
          <input type="checkbox" checked={thresholdOn[t.id] !== false} onChange={(e) => onThreshold(t.id, e.target.checked)} />
          {`${t.label} = ${t.value}`}
          {t.series && <span className={styles.count}> on {t.series}</span>}
        </label>
      ))}
    </div>
  );
}
